import { useEffect, useState } from "react";
import { getShippingMatrix, type ShippingMatrix, type ShippingRateRule } from "./actions";

// Console-visible fix for ISSUES.md #23's own finding: the real shipping
// data (shipping_class x zone x price-tier) is a clean matrix -- what's
// "convoluted" is WooCommerce's own Table Rate Shipping admin UI, which only
// lets you edit one zone-method instance at a time with no cross-zone or
// cross-class view. This renders the whole thing as one grid. Read-only --
// no write path exists yet (staged-first, per the standing constraint).

function cellSummary(rules: ShippingRateRule[] | undefined): string {
  if (!rules || rules.length === 0) return "—";
  if (rules.length === 1) {
    const r = rules[0];
    if (Number(r.cost) > 0) return `$${r.cost}`;
    if (Number(r.costPerItem) > 0) return `$${r.costPerItem}/item`;
    if (Number(r.costPercent) > 0) return `${r.costPercent}%`;
    return "$0";
  }
  const costs = rules.map((r) => Number(r.cost) || Number(r.costPerItem) || 0).filter((c) => c > 0);
  if (costs.length === 0) return `${rules.length} tiers`;
  return `${rules.length} tiers: $${Math.min(...costs)}–$${Math.max(...costs)}`;
}

function ClassLabel({ name }: { name: string }) {
  const clean = name.replace(/_shipping_rates$/i, "").replace(/_/g, " ");
  return <span style={{ textTransform: "capitalize" }}>{clean}</span>;
}

export function ShippingMatrixPanel() {
  const [data, setData] = useState<ShippingMatrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    getShippingMatrix().then((r) => (r.ok ? setData(r.data) : setError(r.error)));
  }, []);

  if (error) return <div className="panel error">Error: {error}</div>;
  if (!data) return <div className="panel">Loading…</div>;

  return (
    <div>
      <h1>Shipping matrix</h1>
      <p className="meta">
        Read-only view of the real shipping_class × zone × price-tier data
        (<code>ISSUES.md</code> #23) — the underlying data is a clean matrix;
        WooCommerce's own Table Rate Shipping admin only lets you edit one
        zone-method at a time with no cross-zone view. No write path yet —
        staged-first, per the standing constraint on this whole effort.
      </p>

      {data.zones.some((z) => z.hasNoEnabledMethod) && (
        <div className="panel error" style={{ marginTop: 12 }}>
          🔴 <strong>Real gap, not cosmetic</strong>: the following zones have{" "}
          <strong>zero enabled shipping methods</strong> — a customer whose
          address falls in one of these has no valid way to ship at checkout:{" "}
          {data.zones.filter((z) => z.hasNoEnabledMethod).map((z) => z.zoneName).join(", ")}
        </div>
      )}

      <div className="tilegrid" style={{ marginTop: 12 }}>
        {data.zones.map((z) => (
          <div key={z.zoneId} className={`tile ${z.hasNoEnabledMethod ? "warn" : ""}`}>
            <div className="tile-value">
              {z.hasNoEnabledMethod ? "NONE" : z.hasFreeShipping ? "Free +" : "Active"}
            </div>
            <div className="tile-label">{z.zoneName}</div>
          </div>
        ))}
      </div>

      <div className="manual-table" style={{ marginTop: 20, overflowX: "auto" }}>
        <table className="manual-products">
          <thead>
            <tr>
              <th>Shipping class (by product count)</th>
              {data.zones.map((z) => <th key={z.zoneId}>{z.zoneName}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.shippingClasses.map((c) => (
              <tr key={c.id}>
                <td>
                  <ClassLabel name={c.name} /> <span className="meta">({c.productCount.toLocaleString()})</span>
                </td>
                {data.zones.map((z) => {
                  const rules = data.matrix[String(z.zoneId)]?.[String(c.id)];
                  const key = `${z.zoneId}-${c.id}`;
                  return (
                    <td key={z.zoneId}>
                      <button
                        disabled={!rules || rules.length === 0}
                        onClick={() => setExpanded(expanded === key ? null : key)}
                        style={{ background: "none", border: "none", cursor: rules?.length ? "pointer" : "default",
                                 color: rules?.length ? "#2563eb" : "#999", padding: 0 }}
                      >
                        {cellSummary(rules)}
                      </button>
                      {expanded === key && rules && (
                        <div style={{ marginTop: 6, fontSize: 12 }}>
                          {rules.map((r, i) => (
                            <div key={i}>
                              {r.min || r.max ? `$${r.min || "0"}–$${r.max || "∞"}` : "any"}: ${r.cost}
                              {Number(r.costPerItem) > 0 ? ` + $${r.costPerItem}/item` : ""}
                              {Number(r.costPercent) > 0 ? ` + ${r.costPercent}%` : ""}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
